import { Box } from '@mui/material';
import ReactECharts, { EChartsOption } from 'echarts-for-react';
import LoaderComponent from 'components/Loader';
import { useMemo } from 'react';

export interface SeverityPieData {
  _id: string;
  totalCount: number;
}

interface SeverityPieProps {
  data: SeverityPieData[];
  isLoading: boolean;
  onSliceClick?: (source: string) => void;
}

const SeverityPieChart = ({ data, isLoading, onSliceClick }: SeverityPieProps) => {
  if (isLoading) return <LoaderComponent />;

  if (!data || data.length === 0) {
    return <Box sx={{ width: '100%', p: 2, textAlign: 'center' }}>No data available</Box>;
  }

  const compactFormatter = useMemo(() => new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 2
  }), []);

  const failureReasonChartData = useMemo(() => {
    return data.map((item) => ({
      value: item.totalCount,
      name: item._id.replaceAll('_', ' ')
    }));
  }, [data]);

  const failureReason: EChartsOption = {
    color: ['#F04438', '#F79009', '#EAAA08', '#FFCC00'],
    backgroundColor: 'transparent',
    tooltip: {
      backgroundColor: '#fff',
      trigger: 'item'
    },
    legend: {
      orient: 'vertical',
      left: '48%',
      top: 'middle',
      itemWidth: 20,
      itemHeight: 20,
      itemStyle: { borderRadius: 0 },
      textStyle: {
        fontSize: 15,
        color: '#100f0f'
      }
    },
    series: [
      {
        type: 'pie',
        radius: '75%',
        center: ['22%', '55%'],
        avoidLabelOverlap: false,
        label: {
          show: true,
          position: 'inside',
          color: '#fff',
          fontSize: 15,
          formatter: (params: any) => {
            const value = Number(params.value ?? 0);
            return `${compactFormatter.format(value)}`;
          }
        },
        data: failureReasonChartData,
        emphasis: {
          itemStyle: {
            shadowBlur: 15,
            shadowOffsetX: 0,
            shadowOffsetY: 8,
            shadowColor: 'rgba(0, 0, 0, 0.25)',
          }
        }
      }
    ]
  };

  return (
    <Box sx={{ flex: 1, position: 'relative' }}>
      <Box
        sx={{
          width: '250px',
          background: 'rgba(0, 0, 0, 0.5)',
          height: '35px',
          left: '22%',
          bottom: '8px',
          position: 'absolute',
          filter: 'blur(25px)',
          transform: 'translateX(-50%)',
          borderRadius: '50%',
          zIndex: 0,
        }}
      />
      <ReactECharts
        option={failureReason}
        style={{ height: 340, position: 'relative', zIndex: 1 }}
        onEvents={{
          click: (params: { name?: string }) => {
            if (params?.name) onSliceClick?.(params.name);
          }
        }}
      />
    </Box>
  );
};

export default SeverityPieChart;
